import { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Button } from './button';
import { cn } from '../../lib/utils';
import { X, ChevronRight, ChevronLeft, MapPin } from 'lucide-react';

/**
 * Interactive Guided Tour engine.
 * Highlights real UI elements with a spotlight overlay + positioned tooltip.
 *
 * Usage:
 * <GuidedTour
 *   steps={[{ selector: '[data-testid="xyz"]', title: 'Step 1', content: '...', position: 'bottom' }]}
 *   active={true}
 *   onComplete={() => {}}
 *   onExit={() => {}}
 * />
 */

export function GuidedTour({ steps = [], active = false, onComplete, onExit }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [targetRect, setTargetRect] = useState(null);
  const [ready, setReady] = useState(false);
  const tooltipRef = useRef(null);

  const step = steps[currentStep];

  const updateRect = useCallback(() => {
    if (!step || !active) return;
    const el = document.querySelector(step.selector);
    if (el) {
      const rect = el.getBoundingClientRect();
      // If element covers >60% of viewport, treat as page-level (no spotlight)
      const viewW = window.innerWidth;
      const viewH = window.innerHeight;
      const coverage = (rect.width * rect.height) / (viewW * viewH);
      if (coverage > 0.6) {
        setTargetRect(null); // No spotlight for page-level elements
      } else {
        setTargetRect({ top: rect.top, left: rect.left, width: rect.width, height: rect.height });
        el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    } else {
      setTargetRect(null);
    }
  }, [step, active]);

  useEffect(() => {
    if (active && step) {
      setReady(false);
      const timeout = setTimeout(() => {
        updateRect();
        // Mark ready after positioning (prevents stray clicks from closing)
        setTimeout(() => setReady(true), 400);
        // Update again after scroll settles
        setTimeout(updateRect, 600);
      }, 500);
      return () => clearTimeout(timeout);
    }
  }, [active, step, currentStep, updateRect]);

  useEffect(() => {
    if (!active) { setCurrentStep(0); setTargetRect(null); setReady(false); }
  }, [active]);

  // Handle Escape key
  useEffect(() => {
    if (!active) return;
    const handleKey = (e) => { if (e.key === 'Escape') { exit(); } };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [active]); // eslint-disable-line react-hooks/exhaustive-deps

  const next = () => {
    if (currentStep < steps.length - 1) setCurrentStep(currentStep + 1);
    else onComplete?.();
  };
  const prev = () => { if (currentStep > 0) setCurrentStep(currentStep - 1); };
  const exit = () => { setCurrentStep(0); setTargetRect(null); onExit?.(); };

  if (!active || !step) return null;

  const pad = 8;
  const spotStyle = targetRect ? {
    top: targetRect.top - pad,
    left: targetRect.left - pad,
    width: targetRect.width + pad * 2,
    height: targetRect.height + pad * 2,
  } : null;

  // Tooltip position
  const pos = step.position || 'bottom';
  let tooltipStyle = {};
  if (targetRect) {
    const gap = 12;
    const maxLeft = window.innerWidth - 360; // tooltip is 340px wide + margin
    if (pos === 'bottom') {
      tooltipStyle = { top: Math.min(targetRect.top + targetRect.height + pad + gap, window.innerHeight - 240), left: Math.max(16, Math.min(targetRect.left, maxLeft)) };
    } else if (pos === 'top') {
      tooltipStyle = { bottom: Math.max(16, window.innerHeight - targetRect.top + pad + gap), left: Math.max(16, Math.min(targetRect.left, maxLeft)) };
    } else if (pos === 'right') {
      tooltipStyle = { top: Math.max(16, targetRect.top), left: Math.min(targetRect.left + targetRect.width + pad + gap, maxLeft) };
    } else if (pos === 'left') {
      tooltipStyle = { top: Math.max(16, targetRect.top), right: Math.max(16, window.innerWidth - targetRect.left + pad + gap) };
    }
  } else {
    // Center tooltip for page-level overview steps
    tooltipStyle = { top: '30%', left: '50%', transform: 'translateX(-50%)' };
  }

  return createPortal(
    <div className="fixed inset-0 z-[9999]" data-testid="guided-tour-overlay">
      {/* Dark overlay - 4 divs around the spotlight */}
      {spotStyle ? (
        <>
          {/* Top */}
          <div className="fixed left-0 right-0 top-0 bg-black/60" style={{ height: Math.max(0, spotStyle.top) }} />
          {/* Bottom */}
          <div className="fixed left-0 right-0 bottom-0 bg-black/60" style={{ top: spotStyle.top + spotStyle.height }} />
          {/* Left */}
          <div className="fixed left-0 bg-black/60" style={{ top: spotStyle.top, height: spotStyle.height, width: Math.max(0, spotStyle.left) }} />
          {/* Right */}
          <div className="fixed right-0 bg-black/60" style={{ top: spotStyle.top, height: spotStyle.height, left: spotStyle.left + spotStyle.width }} />
        </>
      ) : (
        <div className="fixed inset-0 bg-black/60" />
      )}

      {/* Spotlight border glow */}
      {spotStyle && (
        <div
          className="absolute rounded-lg border-2 border-primary shadow-[0_0_0_4px_rgba(59,130,246,0.2)] pointer-events-none"
          style={{ ...spotStyle, animation: 'pulse 2s cubic-bezier(0.4,0,0.6,1) infinite' }}
        />
      )}

      {/* Tooltip */}
      <div
        ref={tooltipRef}
        className="absolute z-[10000] w-[340px] bg-popover border border-border rounded-xl shadow-2xl p-4 space-y-3 animate-in fade-in-0 slide-in-from-bottom-2 duration-300"
        style={{ ...tooltipStyle, pointerEvents: 'auto' }}
        onClick={(e) => e.stopPropagation()}
        data-testid="tour-tooltip"
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
              <MapPin className="w-3.5 h-3.5 text-primary" />
            </div>
            <h4 className="font-semibold text-sm leading-tight">{step.title}</h4>
          </div>
          <button onClick={exit} className="text-muted-foreground hover:text-foreground p-0.5" data-testid="tour-exit">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Content */}
        <p className="text-sm text-muted-foreground leading-relaxed">{step.content}</p>

        {/* Footer */}
        <div className="flex items-center justify-between pt-1">
          <span className="text-xs text-muted-foreground font-medium">
            Step {currentStep + 1} of {steps.length}
          </span>
          <div className="flex items-center gap-1.5">
            {currentStep > 0 && (
              <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={prev} data-testid="tour-prev">
                <ChevronLeft className="w-3.5 h-3.5 mr-0.5" /> Back
              </Button>
            )}
            <Button size="sm" className="h-7 text-xs" onClick={next} data-testid="tour-next">
              {currentStep === steps.length - 1 ? 'Finish' : <>Next <ChevronRight className="w-3.5 h-3.5 ml-0.5" /></>}
            </Button>
          </div>
        </div>

        {/* Progress dots */}
        <div className="flex items-center justify-center gap-1 pt-0.5">
          {steps.map((_, i) => (
            <div key={i} className={cn("w-1.5 h-1.5 rounded-full transition-colors", i === currentStep ? "bg-primary" : i < currentStep ? "bg-primary/40" : "bg-muted-foreground/20")} />
          ))}
        </div>
      </div>
    </div>,
    document.body
  );
}

export default GuidedTour;
